/**
 * ARAM Platform - Router Configuration
 * Core Desarrollos S.A.S - Copyright 2024-2025
 */

import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import { RequirePermission } from '@/components/shared/RequirePermission'
import { RouteErrorPage } from '@/components/shared/RouteErrorPage'
import { useAuthStore } from '@/stores/authStore'

// Layouts
import { RootLayout } from '@/layouts/RootLayout'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { AuthLayout } from '@/layouts/AuthLayout'
import { MobileLayout } from '@/layouts/MobileLayout'
import { ClientPortalLayout } from '@/layouts/ClientPortalLayout'
import { LaundryModuleShell } from '@/components/traceability/LaundryModuleShell'


// Loading component
import { LoadingSpinner } from '@/components/ui/loading-spinner'
// Feria — Sitevinitech 2026 (public, no auth)
const FeriaLandingPage = lazy(() => import('@/pages/feria/FeriaLandingPage'))
const VinedosDemoPage = lazy(() => import('@/pages/feria/VinedosDemoPage'))
const FeriaFlotaPage = lazy(() => import('@/pages/feria/FeriaFlotaPage'))
const FeriaBIPage = lazy(() => import('@/pages/feria/FeriaBIPage'))
const FeriaClimaPage = lazy(() => import('@/pages/feria/FeriaClimaPage'))
const FeriaRFIDPage = lazy(() => import('@/pages/feria/FeriaRFIDPage'))
const CampoTraceabilityPage = lazy(() => import('@/pages/traceability/CampoTraceabilityPage'))

// Lazy load pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const SSOCallbackPage = lazy(() => import('@/pages/auth/SSOCallbackPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const ClientPortalPage = lazy(() => import('@/pages/client/ClientPortalPage'))
const ClientPortalItemsPage = lazy(() => import('@/pages/client/ClientPortalItemsPage'))
const ClientPortalLotDetailPage = lazy(() => import('@/pages/client/ClientPortalLotDetailPage'))
const ClientPortalOperationsPage = lazy(() => import('@/pages/client/ClientPortalOperationsPage'))
const ClientPortalReportsPage = lazy(() => import('@/pages/client/ClientPortalReportsPage'))

// Dashboard
const AnalyticsPage = lazy(() => import('@/pages/dashboard/AnalyticsPage'))
const SuperAdminShowcasePage = lazy(() => import('@/pages/dashboard/SuperAdminShowcasePage'))

// Fleet
const FleetDashboardPage = lazy(() => import('@/pages/fleet/FleetDashboardPage'))
const FleetAgendaPage = lazy(() => import('@/pages/fleet/FleetAgendaPage'))
const VehiclesPage = lazy(() => import('@/pages/fleet/VehiclesPage'))
const VehicleDetailPage = lazy(() => import('@/pages/fleet/VehicleDetailPage'))
const DriversPage = lazy(() => import('@/pages/fleet/DriversPage'))
const DriverDetailPage = lazy(() => import('@/pages/fleet/DriverDetailPage'))
const MaintenancePage = lazy(() => import('@/pages/fleet/MaintenancePage'))
const FleetDevicesPage = lazy(() => import('@/pages/fleet/DevicesPage'))
const TranspondersPage = lazy(() => import('@/pages/fleet/TranspondersPage'))
const FuelPage = lazy(() => import('@/pages/fleet/FuelPage'))
const DocumentsPage = lazy(() => import('@/pages/fleet/DocumentsPage'))
const InspectionPhotosPage = lazy(() => import('@/pages/fleet/InspectionPhotosPage'))

// Orders
const OrdersPage = lazy(() => import('@/pages/orders/OrdersPage'))
const OrderDetailPage = lazy(() => import('@/pages/orders/OrderDetailPage'))
const OrderCreatePage = lazy(() => import('@/pages/orders/OrderCreatePage'))
const OrderImportPage = lazy(() => import('@/pages/orders/OrderImportPage'))

// Routes
const RoutesPage = lazy(() => import('@/pages/routes/RoutesPage'))
const RouteDetailPage = lazy(() => import('@/pages/routes/RouteDetailPage'))
const RouteOptimizePage = lazy(() => import('@/pages/routes/RouteOptimizePage'))
const RoutePlannerPage = lazy(() => import('@/pages/routes/RoutePlannerPage'))
const TransportNewsPage = lazy(() => import('@/pages/routes/TransportNewsPage'))
const InteractiveRoutePage = lazy(() => import('@/pages/routes/InteractiveRoutePage'))

// Tracking
const LiveTrackingPage = lazy(() => import('@/pages/tracking/LiveTrackingPage'))
const GeofencesPage = lazy(() => import('@/pages/tracking/GeofencesPage'))
const AlertsPage = lazy(() => import('@/pages/tracking/AlertsPage'))
const GlobalLogisticsPage = lazy(() => import('@/pages/tracking/GlobalLogisticsPage'))
const ShipmentsPage = lazy(() => import('@/pages/freight/ShipmentsPage'))
const ShipmentDetailPage = lazy(() => import('@/pages/freight/ShipmentDetailPage'))
const NCMClassifierPage = lazy(() => import('@/pages/freight/NCMClassifierPage'))
const FreightRouteEditorPage = lazy(() => import('@/pages/freight/FreightRouteEditorPage'))

// Devices
const DevicesPage = lazy(() => import('@/pages/devices/DevicesPage'))
const DeviceDetailPage = lazy(() => import('@/pages/devices/DeviceDetailPage'))
const TelemetryPage = lazy(() => import('@/pages/devices/TelemetryPage'))
const DeviceProvisioningPage = lazy(() => import('@/pages/devices/DeviceProvisioningPage'))
const IoTLivePage = lazy(() => import('@/pages/devices/IoTLivePage'))

// Integrations
const IntegrationsPage = lazy(() => import('@/pages/integrations/IntegrationsPage'))
const IntegrationDetailsPage = lazy(() => import('@/pages/integrations/IntegrationDetailsPage'))
const MercadoLibrePage = lazy(() => import('@/pages/integrations/MercadoLibrePage'))
const WebhooksPage = lazy(() => import('@/pages/integrations/WebhooksPage'))

// Reports
const ReportsPage = lazy(() => import('@/pages/reports/ReportsPage'))
const ReportDetailPage = lazy(() => import('@/pages/reports/ReportDetailPage'))

// AI
const AIAssistantPage = lazy(() => import('@/pages/ai/AIAssistantPage'))

// Notifications Hub
const NotificationsPage = lazy(() => import('@/pages/notifications/NotificationsPage'))
const CommunicationsPage = lazy(() => import('@/pages/communications/CommunicationsPage'))

// Public Tracking Portal
const TrackingPortalPage = lazy(() => import('@/pages/public/TrackingPortalPage'))

// Maps
const MendozaMapPage = lazy(() => import('@/pages/maps/MendozaMapPage'))
const LoraWanDemoPage = lazy(() => import('@/pages/maps/LoraWanDemoPage'))
const LoraWanProjectionPage = lazy(() => import('@/pages/maps/LoraWanProjectionPage'))
const LoraWanProjectionControlPage = lazy(() => import('@/pages/maps/LoraWanProjectionControlPage'))

// Traffic Intelligence
const TrafficIntelligencePage = lazy(() => import('@/pages/traffic/TrafficIntelligencePage'))

// Weather
const WeatherModule = lazy(() => import('@/pages/weather/WeatherModuleV2'))

// Web Analytics & Social Media
const WebAnalyticsPage = lazy(() => import('@/pages/web-analytics/WebAnalyticsPage'))

// Sustainability
const EmissionsCalculatorPage = lazy(() => import('@/pages/emissions/EmissionsCalculatorPage'))

// Security
const SecurityDashboard = lazy(() => import('@/pages/security/SecurityDashboard'))

// Última Milla
const LastMilePage = lazy(() => import('@/pages/last-mile/LastMilePage'))

// ARAM Distribution
const DistributionWorkspacePage = lazy(() => import('@/pages/distribution/DistributionWorkspacePage'))
const DistributionCustomerDetailPage = lazy(() => import('@/pages/distribution/CustomerDetailPage'))
const IndustrialSolutionsPage = lazy(() => import('@/pages/industrial/IndustrialSolutionsPage'))
const IndustrialInventoryCountsPage = lazy(() => import('@/pages/industrial/IndustrialInventoryCountsPage'))
const WmsPage = lazy(() => import('@/pages/warehouse/WmsPage'))

// Risk Intelligence
const RiskDashboardPage = lazy(() => import('@/pages/risk/RiskDashboardPage'))

// Fiscal — ARCA & Aduana
const FiscalDashboardPage = lazy(() => import('@/pages/fiscal/FiscalDashboardPage'))

// Tesorería
const TreasuryDashboardPage = lazy(() => import('@/pages/treasury/TreasuryDashboardPage'))

// Fincas & Campo
const FincasDashboardPage = lazy(() => import('@/pages/fincas/FincasDashboardPage'))

// Procesadora & Empaque
const ProcesadoraDashboardPage = lazy(() => import('@/pages/procesadora/ProcesadoraDashboardPage'))

// Minería
const MiningDashboardPage = lazy(() => import('@/pages/mining/MiningDashboardPage'))
const ExpedicionesDashboardPage = lazy(() => import('@/pages/mining/ExpedicionesDashboardPage'))
const ServiciosDashboardPage = lazy(() => import('@/pages/mining/ServiciosDashboardPage'))

// Drones & Rutas Aéreas
const DronesDashboardPage = lazy(() => import('@/pages/drones/DronesDashboardPage'))

// Trazabilidad RFID
const TraceabilityDashboardPage = lazy(() => import('@/pages/traceability/TraceabilityDashboardPage'))
const TagRegistryPage = lazy(() => import('@/pages/traceability/core/TagRegistryPage'))
const CommissioningStationPage = lazy(() => import('@/pages/traceability/core/CommissioningStationPage'))
const EventLogPage = lazy(() => import('@/pages/traceability/core/EventLogPage'))
const ReadingStationPage = lazy(() => import('@/pages/traceability/core/ReadingStationPage'))
const RfidLabPage = lazy(() => import('@/pages/traceability/core/RfidLabPage'))
const RentalPage = lazy(() => import('@/pages/rental/RentalPage'))
const RentalOperationsPage = lazy(() => import('@/pages/rental/RentalOperationsPage'))
const RentalAssetDetailPage = lazy(() => import('@/pages/rental/RentalAssetDetailPage'))
const RentalDistributedPage = lazy(() => import('@/pages/rental/RentalDistributedPage'))
const RentalAssetsPage = lazy(() => import('@/pages/rental/RentalAssetsPage'))
const RentalContractsPage = lazy(() => import('@/pages/rental/RentalContractsPage'))
const RentalReturnsPage = lazy(() => import('@/pages/rental/RentalReturnsPage'))
const RentalCustomersPage = lazy(() => import('@/pages/rental/RentalCustomersPage'))
const RentalReportsPage = lazy(() => import('@/pages/rental/RentalReportsPage'))
const RentalPricingPage = lazy(() => import('@/pages/rental/RentalPricingPage'))
const RentalIncidentsPage = lazy(() => import('@/pages/rental/RentalIncidentsPage'))
const LaundryDashboardPage = lazy(() => import('@/pages/traceability/laundry/LaundryDashboardPage'))
const TextileItemsPage = lazy(() => import('@/pages/traceability/laundry/TextileItemsPage'))
const TextileItemDetailPage = lazy(() => import('@/pages/traceability/laundry/TextileItemDetailPage'))
const LaundryReceptionPage = lazy(() => import('@/pages/traceability/laundry/LaundryReceptionPage'))
const LaundryOrdersPage = lazy(() => import('@/pages/traceability/laundry/LaundryOrdersPage'))
const LaundryDispatchPage = lazy(() => import('@/pages/traceability/laundry/LaundryDispatchPage'))
const LaundryCustomersPage = lazy(() => import('@/pages/traceability/laundry/LaundryCustomersPage'))
const LaundryIncidentsPage = lazy(() => import('@/pages/traceability/laundry/LaundryIncidentsPage'))
const LaundryReportsPage = lazy(() => import('@/pages/traceability/laundry/LaundryReportsPage'))
const AgroDashboardPage = lazy(() => import('@/pages/traceability/agro/AgroDashboardPage'))
const CosechaPage = lazy(() => import('@/pages/traceability/agro/CosechaPage'))
const ClasificacionPage = lazy(() => import('@/pages/traceability/agro/ClasificacionPage'))
const EmpaquePage = lazy(() => import('@/pages/traceability/agro/EmpaquePage'))
const AlmacenamientoPage = lazy(() => import('@/pages/traceability/agro/AlmacenamientoPage'))
const DespachoAgroPage = lazy(() => import('@/pages/traceability/agro/DespachoAgroPage'))
const LoteDetailPage = lazy(() => import('@/pages/traceability/agro/LoteDetailPage'))

// Document Signing (DocuSeal)
const DocumentsSigningPage = lazy(() => import('@/pages/documents/DocumentsSigningPage'))

// Mobile Handheld RFID — Chafon H105 (keyboard-wedge + PWA)
const MobileRecepcionPage   = lazy(() => import('@/pages/mobile/MobileRecepcionPage'))
const MobileCicloPage       = lazy(() => import('@/pages/mobile/MobileCicloPage'))
const MobileEventoPage      = lazy(() => import('@/pages/mobile/MobileEventoPage'))
const MobileDespachoPage    = lazy(() => import('@/pages/mobile/MobileDespachoPage'))
const MobileDriverHomePage  = lazy(() => import('@/pages/mobile/MobileDriverHomePage'))
const MobileColeccionPage   = lazy(() => import('@/pages/mobile/MobileColeccionPage'))
const MobileHomePage        = lazy(() => import('@/pages/mobile/MobileHomePage'))
const MobileConnectPage     = lazy(() => import('@/pages/mobile/MobileConnectPage'))
const MobileH105LogisticsPage = lazy(() => import('@/pages/mobile/MobileH105LogisticsPage'))
const MobileH105StockPage = lazy(() => import('@/pages/mobile/MobileH105StockPage'))
const MobileH105WarehousePage = lazy(() => import('@/pages/mobile/MobileH105DemoPages').then(module => ({ default: module.MobileH105WarehousePage })))
const MobileH105SalesPage = lazy(() => import('@/pages/mobile/MobileH105DemoPages').then(module => ({ default: module.MobileH105SalesPage })))
const MobileH105RfidPage = lazy(() => import('@/pages/mobile/MobileH105RfidPage'))
const MobileH105DiagnosticsPage = lazy(() => import('@/pages/mobile/MobileH105DemoPages').then(module => ({ default: module.MobileH105DiagnosticsPage })))

// Microapp Handheld WMS — Módulos Operativos Demo
const MobileWmsLoginPage   = lazy(() => import('@/pages/mobile/wms/MobileWmsLoginPage'))
const MobileWmsMenuPage    = lazy(() => import('@/pages/mobile/wms/MobileWmsMenuPage'))
const MobileWmsPickingPage = lazy(() => import('@/pages/mobile/wms/MobileWmsPickingPage'))
const MobileWmsReceivePage = lazy(() => import('@/pages/mobile/wms/MobileWmsReceivePage'))
const MobileWmsMovePage    = lazy(() => import('@/pages/mobile/wms/MobileWmsMovePage'))
const MobileWmsQueryPage   = lazy(() => import('@/pages/mobile/wms/MobileWmsQueryPage'))

// Laundry Collection Desktop
const LaundryCollectionPage = lazy(() => import('@/pages/traceability/laundry/LaundryCollectionPage'))

// HR Module
const HRDashboardPage = lazy(() => import('@/pages/hr/HRDashboardPage'))
const HRAgendaPage = lazy(() => import('@/pages/hr/HRAgendaPage'))
const HREmployeesPage = lazy(() => import('@/pages/hr/EmployeesPage'))
const HRAttendancePage = lazy(() => import('@/pages/hr/AttendancePage'))
const HRPayrollPage = lazy(() => import('@/pages/hr/PayrollPage'))
const HROrgChartPage = lazy(() => import('@/pages/hr/OrgChartPage'))
const PeopleHomePage = lazy(() => import('@/pages/hr/PeopleHomePage'))
const PeopleRequestsPage = lazy(() => import('@/pages/hr/PeopleRequestsPage'))
const PeopleDirectoryPage = lazy(() => import('@/pages/hr/PeopleDirectoryPage'))
const PeopleProfilePage = lazy(() => import('@/pages/hr/PeopleProfilePage'))
const PeopleApprovalsPage = lazy(() => import('@/pages/hr/PeopleApprovalsPage'))
const PeopleCommunityPage = lazy(() => import('@/pages/hr/PeopleCommunityPage'))
const PeopleNotificationsPage = lazy(() => import('@/pages/hr/PeopleNotificationsPage'))
const PeopleEditorialPage = lazy(() => import('@/pages/hr/PeopleEditorialPage'))
const PeopleDocumentsPage = lazy(() => import('@/pages/hr/PeopleDocumentsPage'))
const PeopleJourneysPage = lazy(() => import('@/pages/hr/PeopleJourneysPage'))
const PeopleGrowthPage = lazy(() => import('@/pages/hr/PeopleGrowthPage'))
const PeopleTalentPage = lazy(() => import('@/pages/hr/PeopleTalentPage'))
const PeopleOperationsPage = lazy(() => import('@/pages/hr/PeopleOperationsPage'))
const PeopleOperationsAdminPage = lazy(() => import('@/pages/hr/PeopleOperationsAdminPage'))
const PeopleBenefitsPage = lazy(() => import('@/pages/hr/PeopleBenefitsPage'))
const PeopleOpportunitiesPage = lazy(() => import('@/pages/hr/PeopleOpportunitiesPage'))
const PeopleEngagementAdminPage = lazy(() => import('@/pages/hr/PeopleEngagementAdminPage'))
const PeopleAssistantPage = lazy(() => import('@/pages/hr/PeopleAssistantPage'))
const PeopleAnalyticsPage = lazy(() => import('@/pages/hr/PeopleAnalyticsPage'))
const PeopleIntegrationsPage = lazy(() => import('@/pages/hr/PeopleIntegrationsPage'))

// Projects Module
const ProjectsDashboardPage = lazy(() => import('@/pages/projects/ProjectsDashboardPage'))
const ProjectsAgendaPage = lazy(() => import('@/pages/projects/ProjectsAgendaPage'))
const ProjectsPage = lazy(() => import('@/pages/projects/ProjectsPage'))
const BoardPage = lazy(() => import('@/pages/projects/BoardPage'))
const ProjectWorkspacePage = lazy(() => import('@/pages/projects/ProjectWorkspacePage'))
const ProjectFieldPage = lazy(() => import('@/pages/projects/ProjectFieldPage'))
const ProjectControlPage = lazy(() => import('@/pages/projects/ProjectControlPage'))

// Fixed Assets Module
const FixedAssetsPage = lazy(() => import('@/pages/fixed_assets/FixedAssetsPage'))
const FixedAssetsDashboardPage = lazy(() => import('@/pages/fixed_assets/FixedAssetsDashboardPage'))
const FixedAssetsReportPage = lazy(() => import('@/pages/fixed_assets/FixedAssetsReportPage'))
const ActivationRequestPage = lazy(() => import('@/pages/fixed_assets/ActivationRequestPage'))

// Accounting Module
const AccountingDashboardPage = lazy(() => import('@/pages/accounting/AccountingDashboardPage'))
const AccountingAgendaPage = lazy(() => import('@/pages/accounting/AccountingAgendaPage'))
const AccountingInvoicesPage = lazy(() => import('@/pages/accounting/InvoicesPage'))
const AccountingBillsPage = lazy(() => import('@/pages/accounting/BillsPage'))
const AccountingAssetsPage = lazy(() => import('@/pages/accounting/AssetsPage'))
const AccountingBudgetPage = lazy(() => import('@/pages/accounting/BudgetPage'))
const AccountingAccountsPage = lazy(() => import('@/pages/accounting/AccountsPage'))
const AccountingReportsPage = lazy(() => import('@/pages/accounting/ReportsPage'))
const AccountingPaymentsPage = lazy(() => import('@/pages/accounting/PaymentsPage'))

// Settings
const SettingsPage = lazy(() => import('@/pages/settings/SettingsPage'))
const ProfilePage = lazy(() => import('@/pages/settings/ProfilePage'))
const UsersPage = lazy(() => import('@/pages/settings/UsersPage'))
const RolesPage = lazy(() => import('@/pages/settings/RolesPage'))
const OrganizationsPage = lazy(() => import('@/pages/settings/OrganizationsPage'))
const NotificationsSettingsPage = lazy(() => import('@/pages/settings/NotificationsSettingsPage'))
const MultiTenancyInfoPage = lazy(() => import('@/pages/settings/MultiTenancyInfoPage'))
const MultiTenancyAuditPage = lazy(() => import('@/pages/settings/MultiTenancyAuditPage'))
const MultiTenancyOverviewPage = lazy(() => import('@/pages/settings/MultiTenancyOverviewPage'))
const VerticalSettingsPage = lazy(() => import('@/pages/settings/VerticalSettingsPage'))
const QRGeneratorPage = lazy(() => import('@/pages/settings/QRGeneratorPage'))
const UserMenuPermissionsPage = lazy(() => import('@/pages/settings/UserMenuPermissionsPage'))
const CodeIntelligencePage = lazy(() => import('@/pages/settings/CodeIntelligencePage'))

// Error pages
const NotFoundPage = lazy(() => import('@/pages/errors/NotFoundPage'))
const UnauthorizedPage = lazy(() => import('@/pages/errors/UnauthorizedPage'))

// Wrapper for lazy components
const LazyPage = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<LoadingSpinner />}>
    {children}
  </Suspense>
)

/** Superadmins enter the demo launcher; regular users keep their configured home. */
function SmartRedirect() {
  const { user } = useAuthStore()
  const target = user?.is_superuser ? '/showcase' : user?.home_path || '/fleet/dashboard'
  return <Navigate to={target} replace />
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorPage />,
    children: [
      // Auth routes
      {
        path: 'auth',
        element: <AuthLayout />,
        children: [
          { path: 'login', element: <LazyPage><LoginPage /></LazyPage> },
          { path: 'sso/callback', element: <LazyPage><SSOCallbackPage /></LazyPage> },
          { path: 'forgot-password', element: <LazyPage><ForgotPasswordPage /></LazyPage> },
          { path: 'reset-password', element: <LazyPage><ResetPasswordPage /></LazyPage> },
        ],
      },

      {
        path: 'client',
        element: <ClientPortalLayout />,
        children: [
          { index: true, element: <LazyPage><ClientPortalPage /></LazyPage> },
          { path: 'prendas', element: <LazyPage><ClientPortalItemsPage /></LazyPage> },
          { path: 'lotes/:lotId', element: <LazyPage><ClientPortalLotDetailPage /></LazyPage> },
          { path: 'intercambios', element: <LazyPage><ClientPortalOperationsPage /></LazyPage> },
          { path: 'reportes', element: <LazyPage><ClientPortalReportsPage /></LazyPage> },
        ],
      },
      
      // Dashboard routes (protected)
      {
        path: '',
        element: <DashboardLayout />,
        children: [
          { index: true, element: <SmartRedirect /> },
          { path: 'showcase', element: <LazyPage><SuperAdminShowcasePage /></LazyPage> },
          
          // Dashboard (legacy redirect)
          { path: 'dashboard', element: <Navigate to="/fleet/dashboard" replace /> },
          { path: 'calendar', element: <Navigate to="/fleet/agenda" replace /> },
          { path: 'analytics', element: <LazyPage><AnalyticsPage /></LazyPage> },
          
          // Fleet Management
          { path: 'fleet/dashboard', element: <LazyPage><FleetDashboardPage /></LazyPage> },
          { path: 'fleet/agenda', element: <LazyPage><FleetAgendaPage /></LazyPage> },
          { path: 'fleet/vehicles', element: <LazyPage><VehiclesPage /></LazyPage> },
          { path: 'fleet/vehicles/:id', element: <LazyPage><VehicleDetailPage /></LazyPage> },
          { path: 'fleet/drivers', element: <LazyPage><DriversPage /></LazyPage> },
          { path: 'fleet/drivers/:id', element: <LazyPage><DriverDetailPage /></LazyPage> },
          { path: 'fleet/maintenance', element: <LazyPage><MaintenancePage /></LazyPage> },
          { path: 'fleet/devices', element: <LazyPage><FleetDevicesPage /></LazyPage> },
          { path: 'fleet/transponders', element: <LazyPage><TranspondersPage /></LazyPage> },
          { path: 'fleet/fuel', element: <LazyPage><FuelPage /></LazyPage> },
          { path: 'fleet/documents', element: <LazyPage><DocumentsPage /></LazyPage> },
          { path: 'fleet/inspection-photos', element: <LazyPage><InspectionPhotosPage /></LazyPage> },
          
          // Orders
          { path: 'orders', element: <LazyPage><OrdersPage /></LazyPage> },
          { path: 'orders/new', element: <LazyPage><OrderCreatePage /></LazyPage> },
          { path: 'orders/import', element: <LazyPage><OrderImportPage /></LazyPage> },
          { path: 'orders/:id', element: <LazyPage><OrderDetailPage /></LazyPage> },
          
          // Routes
          { path: 'routes', element: <LazyPage><RoutesPage /></LazyPage> },
          { path: 'routes/interactive', element: <LazyPage><InteractiveRoutePage /></LazyPage> },
          { path: 'routes/planner', element: <LazyPage><RoutePlannerPage /></LazyPage> },
          { path: 'routes/news', element: <LazyPage><TransportNewsPage /></LazyPage> },
          { path: 'routes/optimize', element: <LazyPage><RouteOptimizePage /></LazyPage> },
          { path: 'routes/:id', element: <LazyPage><RouteDetailPage /></LazyPage> },
          
          // Freight Operations
          { path: 'freight', element: <LazyPage><ShipmentsPage /></LazyPage> },
          { path: 'freight/import', element: <LazyPage><ShipmentsPage /></LazyPage> },
          { path: 'freight/export', element: <LazyPage><ShipmentsPage /></LazyPage> },
          { path: 'freight/mro', element: <LazyPage><ShipmentsPage /></LazyPage> },
          { path: 'freight/last-mile', element: <LazyPage><ShipmentsPage /></LazyPage> },
          { path: 'freight/global', element: <LazyPage><GlobalLogisticsPage /></LazyPage> },
          { path: 'freight/ncm-classifier', element: <LazyPage><NCMClassifierPage /></LazyPage> },
          { path: 'freight/route-editor', element: <LazyPage><FreightRouteEditorPage /></LazyPage> },

          // Tracking
          { path: 'tracking/live', element: <LazyPage><LiveTrackingPage /></LazyPage> },
          { path: 'tracking/geofences', element: <LazyPage><GeofencesPage /></LazyPage> },
          { path: 'tracking/alerts', element: <LazyPage><AlertsPage /></LazyPage> },

          { path: 'tracking/shipments', element: <LazyPage><ShipmentsPage /></LazyPage> },
          { path: 'tracking/shipment/:id', element: <LazyPage><ShipmentDetailPage /></LazyPage> },
          
          // Devices & Telemetry
          { path: 'devices', element: <LazyPage><DevicesPage /></LazyPage> },
          { path: 'devices/:id', element: <LazyPage><DeviceDetailPage /></LazyPage> },
          { path: 'telemetry', element: <LazyPage><TelemetryPage /></LazyPage> },
          { path: 'iot/live', element: <LazyPage><IoTLivePage /></LazyPage> },
          { path: 'devices/provisioning', element: <LazyPage><DeviceProvisioningPage /></LazyPage> },
          
          // Integrations
          { path: 'integrations', element: <LazyPage><IntegrationsPage /></LazyPage> },
          { path: 'integration-details', element: <LazyPage><IntegrationDetailsPage /></LazyPage> },
          { path: 'integrations/mercadolibre', element: <LazyPage><MercadoLibrePage /></LazyPage> },
          { path: 'integrations/webhooks', element: <LazyPage><WebhooksPage /></LazyPage> },
          
          // Reports
          { path: 'reports', element: <LazyPage><ReportsPage /></LazyPage> },
          { path: 'reports/:id', element: <LazyPage><ReportDetailPage /></LazyPage> },
          
          // AI Assistant
          { path: 'ai', element: <LazyPage><AIAssistantPage /></LazyPage> },

          // Notifications Hub (inbox)
          { path: 'notifications', element: <LazyPage><NotificationsPage /></LazyPage> },
          { path: 'communications', element: <LazyPage><RequirePermission permission="communications.view"><CommunicationsPage /></RequirePermission></LazyPage> },
          
          // Maps
          { path: 'maps/mendoza', element: <LazyPage><MendozaMapPage /></LazyPage> },
          { path: 'maps/lorawan-demo', element: <LazyPage><LoraWanDemoPage /></LazyPage> },
          { path: 'maps/lorawan-projection', element: <LazyPage><LoraWanProjectionPage /></LazyPage> },
          { path: 'maps/lorawan-projection/control', element: <LazyPage><LoraWanProjectionControlPage /></LazyPage> },
          
          // Traffic Intelligence
          { path: 'traffic', element: <LazyPage><TrafficIntelligencePage /></LazyPage> },
          { path: 'traffic/intelligence', element: <LazyPage><TrafficIntelligencePage /></LazyPage> },

          // Fiscal — ARCA & Aduana
          { path: 'fiscal', element: <LazyPage><FiscalDashboardPage /></LazyPage> },
          { path: 'fiscal/comprobantes', element: <LazyPage><FiscalDashboardPage /></LazyPage> },
          { path: 'fiscal/aduana', element: <LazyPage><FiscalDashboardPage /></LazyPage> },
          { path: 'fiscal/libro-iva', element: <LazyPage><FiscalDashboardPage /></LazyPage> },
          { path: 'fiscal/credenciales', element: <LazyPage><FiscalDashboardPage /></LazyPage> },
          { path: 'fiscal/simulador', element: <LazyPage><FiscalDashboardPage /></LazyPage> },

          // Tesorería
          { path: 'treasury', element: <LazyPage><TreasuryDashboardPage /></LazyPage> },
          { path: 'treasury/cuentas', element: <LazyPage><TreasuryDashboardPage /></LazyPage> },
          { path: 'treasury/movimientos', element: <LazyPage><TreasuryDashboardPage /></LazyPage> },
          { path: 'treasury/cheques', element: <LazyPage><TreasuryDashboardPage /></LazyPage> },
          { path: 'treasury/vencimientos', element: <LazyPage><TreasuryDashboardPage /></LazyPage> },
          { path: 'treasury/cajas', element: <LazyPage><TreasuryDashboardPage /></LazyPage> },

          // Fincas & Campo
          { path: 'fincas', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/parcelas', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/cultivos', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/sensores', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/alertas', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/activos', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/trazabilidad', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/cuaderno', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/meteorologia', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/satelital', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/mercado', element: <LazyPage><FincasDashboardPage /></LazyPage> },
          { path: 'fincas/cartas-porte', element: <LazyPage><FincasDashboardPage /></LazyPage> },

          // Procesadora & Empaque
          { path: 'procesadora', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/recepcion', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/lineas', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/empaque', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/almacen', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/despacho', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/trazabilidad', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },
          { path: 'procesadora/calidad', element: <LazyPage><ProcesadoraDashboardPage /></LazyPage> },

          // Minería — paths deben coincidir con TABS en MiningDashboardPage.tsx
          { path: 'mining', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/projects', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/expeditions', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/personal', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/transporte', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/panol', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/mro', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/viandas', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/limpieza', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/finanzas', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/gestion', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/telemetry', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/ecosystem', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/lifecycle', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/assets3d', element: <LazyPage><MiningDashboardPage /></LazyPage> },
          { path: 'mining/assets3d/sites', element: <LazyPage><MiningDashboardPage /></LazyPage> },

          // Expediciones Mineras
          { path: 'mining/expediciones', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/proyectos', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/lista', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/personal', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/viajes', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/herramental', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/agenda', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/mapa', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },
          { path: 'mining/expediciones/avisos', element: <LazyPage><ExpedicionesDashboardPage /></LazyPage> },

          // Servicios en Sitio
          { path: 'mining/servicios', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/movilidad', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/conteo', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/mapa', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/catering', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/paniol', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/vehiculos', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/proveedores', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },
          { path: 'mining/servicios/agenda', element: <LazyPage><ServiciosDashboardPage /></LazyPage> },

          // Trazabilidad RFID
          { path: 'traceability', element: <LazyPage><TraceabilityDashboardPage /></LazyPage> },
          { path: 'traceability/tags', element: <LazyPage><TagRegistryPage /></LazyPage> },
          { path: 'traceability/commissioning', element: <LazyPage><CommissioningStationPage /></LazyPage> },
          { path: 'traceability/events', element: <LazyPage><EventLogPage /></LazyPage> },
          { path: 'traceability/reading', element: <LazyPage><ReadingStationPage /></LazyPage> },
          { path: 'traceability/lab', element: <LazyPage><RfidLabPage /></LazyPage> },
          { path: 'rental', element: <LazyPage><RentalPage /></LazyPage> },
          { path: 'rental/assets', element: <LazyPage><RentalAssetsPage /></LazyPage> },
          { path: 'rental/assets/:assetId', element: <LazyPage><RentalAssetDetailPage /></LazyPage> },
          { path: 'rental/contracts', element: <LazyPage><RentalContractsPage /></LazyPage> },
          { path: 'rental/customers', element: <LazyPage><RentalCustomersPage /></LazyPage> },
          { path: 'rental/returns', element: <LazyPage><RentalReturnsPage /></LazyPage> },
          { path: 'rental/operations', element: <LazyPage><RentalOperationsPage /></LazyPage> },
          { path: 'rental/network', element: <LazyPage><RentalDistributedPage /></LazyPage> },
          { path: 'rental/reports', element: <LazyPage><RentalReportsPage /></LazyPage> },
          { path: 'rental/pricing', element: <LazyPage><RentalPricingPage /></LazyPage> },
          { path: 'rental/incidents', element: <LazyPage><RentalIncidentsPage /></LazyPage> },
          {
            path: 'traceability/laundry',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryDashboardPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/recorridos',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryCollectionPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/coleccion',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryCollectionPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/prendas',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <TextileItemsPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/prendas/:id',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <TextileItemDetailPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/items',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <TextileItemsPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/items/:id',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <TextileItemDetailPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/recepcion',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryReceptionPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/ordenes',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryOrdersPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/despacho',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryDispatchPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/clientes',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryCustomersPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/incidentes',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryIncidentsPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },
          {
            path: 'traceability/laundry/reportes',
            element: (
              <LazyPage>
                <LaundryModuleShell>
                  <LaundryReportsPage />
                </LaundryModuleShell>
              </LazyPage>
            ),
          },

          // Trazabilidad Agroalimentaria
          { path: 'traceability/agro', element: <LazyPage><AgroDashboardPage /></LazyPage> },
          { path: 'traceability/agro/cosecha', element: <LazyPage><CosechaPage /></LazyPage> },
          { path: 'traceability/agro/clasificacion', element: <LazyPage><ClasificacionPage /></LazyPage> },
          { path: 'traceability/agro/empaque', element: <LazyPage><EmpaquePage /></LazyPage> },
          { path: 'traceability/agro/almacenamiento', element: <LazyPage><AlmacenamientoPage /></LazyPage> },
          { path: 'traceability/agro/despacho', element: <LazyPage><DespachoAgroPage /></LazyPage> },
          { path: 'traceability/agro/lote/:id', element: <LazyPage><LoteDetailPage /></LazyPage> },

          // Document Signing (DocuSeal)
          { path: 'documents/signing', element: <LazyPage><RequirePermission anyOf={['hr:documents', 'hr:manage', 'hr:sensitive']}><DocumentsSigningPage /></RequirePermission></LazyPage> },

          // HR Module
          { path: 'people', element: <LazyPage><PeopleHomePage /></LazyPage> },
          { path: 'people/me', element: <LazyPage><PeopleProfilePage /></LazyPage> },
          { path: 'people/directory', element: <LazyPage><PeopleDirectoryPage /></LazyPage> },
          { path: 'people/community', element: <LazyPage><PeopleCommunityPage /></LazyPage> },
          { path: 'people/notifications', element: <LazyPage><PeopleNotificationsPage /></LazyPage> },
          { path: 'people/editorial', element: <LazyPage><PeopleEditorialPage /></LazyPage> },
          { path: 'people/documents', element: <LazyPage><PeopleDocumentsPage /></LazyPage> },
          { path: 'people/journeys', element: <LazyPage><RequirePermission anyOf={['hr:workflows', 'hr:manage', 'hr:sensitive']}><PeopleJourneysPage /></RequirePermission></LazyPage> },
          { path: 'people/growth', element: <LazyPage><PeopleGrowthPage /></LazyPage> },
          { path: 'people/talent', element: <LazyPage><RequirePermission anyOf={['hr:talent', 'hr:manage', 'hr:sensitive']}><PeopleTalentPage /></RequirePermission></LazyPage> },
          { path: 'people/operations', element: <LazyPage><PeopleOperationsPage /></LazyPage> },
          { path: 'people/operations/manage', element: <LazyPage><RequirePermission anyOf={['hr:operations', 'hr:manage', 'hr:sensitive']}><PeopleOperationsAdminPage /></RequirePermission></LazyPage> },
          { path: 'people/benefits', element: <LazyPage><PeopleBenefitsPage /></LazyPage> },
          { path: 'people/opportunities', element: <LazyPage><PeopleOpportunitiesPage /></LazyPage> },
          { path: 'people/engagement/manage', element: <LazyPage><RequirePermission anyOf={['hr:engagement', 'hr:manage', 'hr:sensitive']}><PeopleEngagementAdminPage /></RequirePermission></LazyPage> },
          { path: 'people/assistant', element: <LazyPage><PeopleAssistantPage /></LazyPage> },
          { path: 'people/analytics', element: <LazyPage><RequirePermission anyOf={['hr:analytics', 'hr:manage', 'hr:sensitive']}><PeopleAnalyticsPage /></RequirePermission></LazyPage> },
          { path: 'people/integrations', element: <LazyPage><RequirePermission anyOf={['hr:integrations', 'hr:manage', 'hr:sensitive']}><PeopleIntegrationsPage /></RequirePermission></LazyPage> },
          { path: 'people/requests', element: <LazyPage><PeopleRequestsPage /></LazyPage> },
          { path: 'people/approvals', element: <LazyPage><PeopleApprovalsPage /></LazyPage> },
          { path: 'hr/dashboard', element: <LazyPage><HRDashboardPage /></LazyPage> },
          { path: 'hr/agenda', element: <LazyPage><HRAgendaPage /></LazyPage> },
          { path: 'hr/employees', element: <LazyPage><HREmployeesPage /></LazyPage> },
          { path: 'hr/attendance', element: <LazyPage><HRAttendancePage /></LazyPage> },
          { path: 'hr/payroll', element: <LazyPage><HRPayrollPage /></LazyPage> },
          { path: 'hr/org-chart', element: <LazyPage><HROrgChartPage /></LazyPage> },

          // Projects Module
          { path: 'projects/dashboard', element: <LazyPage><ProjectsDashboardPage /></LazyPage> },
          { path: 'projects/agenda', element: <LazyPage><ProjectsAgendaPage /></LazyPage> },
          { path: 'projects', element: <LazyPage><ProjectsPage /></LazyPage> },
          { path: 'projects/board', element: <LazyPage><BoardPage /></LazyPage> },
          { path: 'projects/:projectId/workspace', element: <LazyPage><ProjectWorkspacePage /></LazyPage> },
          { path: 'projects/:projectId/field', element: <LazyPage><ProjectFieldPage /></LazyPage> },
          { path: 'projects/:projectId/control', element: <LazyPage><ProjectControlPage /></LazyPage> },

          // Fixed Assets Module
          { path: 'fixed-assets/dashboard',    element: <LazyPage><FixedAssetsDashboardPage /></LazyPage> },
          { path: 'fixed-assets/report',        element: <LazyPage><FixedAssetsReportPage /></LazyPage> },
          { path: 'fixed-assets/activaciones',  element: <LazyPage><ActivationRequestPage /></LazyPage> },
          { path: 'fixed-assets',               element: <LazyPage><FixedAssetsPage /></LazyPage> },

          // Accounting Module
          { path: 'accounting/dashboard', element: <LazyPage><AccountingDashboardPage /></LazyPage> },
          { path: 'accounting/agenda', element: <LazyPage><AccountingAgendaPage /></LazyPage> },
          { path: 'accounting/invoices', element: <LazyPage><AccountingInvoicesPage /></LazyPage> },
          { path: 'accounting/bills', element: <LazyPage><AccountingBillsPage /></LazyPage> },
          { path: 'accounting/assets', element: <LazyPage><AccountingAssetsPage /></LazyPage> },
          { path: 'accounting/budget', element: <LazyPage><AccountingBudgetPage /></LazyPage> },
          { path: 'accounting/accounts', element: <LazyPage><AccountingAccountsPage /></LazyPage> },
          { path: 'accounting/reports', element: <LazyPage><AccountingReportsPage /></LazyPage> },
          { path: 'accounting/payments', element: <LazyPage><AccountingPaymentsPage /></LazyPage> },

          // Drones & Rutas Aéreas
          { path: 'drones', element: <LazyPage><DronesDashboardPage /></LazyPage> },
          { path: 'drones/flota', element: <LazyPage><DronesDashboardPage /></LazyPage> },
          { path: 'drones/vuelos', element: <LazyPage><DronesDashboardPage /></LazyPage> },
          { path: 'drones/rutas', element: <LazyPage><DronesDashboardPage /></LazyPage> },
          { path: 'drones/archivos', element: <LazyPage><DronesDashboardPage /></LazyPage> },
          { path: 'drones/vision-ia', element: <LazyPage><DronesDashboardPage /></LazyPage> },
          { path: 'drones/marcas', element: <LazyPage><DronesDashboardPage /></LazyPage> },

          // Última Milla (ZipNova)
          { path: 'last-mile', element: <LazyPage><LastMilePage /></LazyPage> },
          { path: 'distribution', element: <LazyPage><RequirePermission anyOf={['distribution.customers.view', 'distribution.catalog.view', 'distribution.pricing.view', 'distribution.sales.view', 'distribution.collections.view', 'distribution.checks.view', 'distribution.tax.view', 'distribution.integrations.view', 'distribution.exceptions.view', 'distribution.returns.view', 'distribution.acopio.view', 'distribution.warehouses.view', 'distribution.procurement.view']}><DistributionWorkspacePage module="dashboard" /></RequirePermission></LazyPage> },
          { path: 'distribution/orders', element: <LazyPage><RequirePermission anyOf={['distribution.sales.view', 'distribution.quotes.view']}><DistributionWorkspacePage module="orders" /></RequirePermission></LazyPage> },
          { path: 'distribution/stock', element: <LazyPage><RequirePermission anyOf={['distribution.warehouses.view', 'distribution.acopio.view', 'distribution.stock.handheld']}><DistributionWorkspacePage module="stock" /></RequirePermission></LazyPage> },
          { path: 'distribution/routes', element: <LazyPage><RequirePermission anyOf={['distribution.sales.view', 'distribution.warehouses.view']}><DistributionWorkspacePage module="routes" /></RequirePermission></LazyPage> },
          { path: 'distribution/deliveries', element: <LazyPage><RequirePermission anyOf={['distribution.sales.view', 'distribution.exceptions.view', 'distribution.returns.view']}><DistributionWorkspacePage module="deliveries" /></RequirePermission></LazyPage> },
          { path: 'distribution/customers', element: <LazyPage><RequirePermission permission="distribution.customers.view"><DistributionWorkspacePage module="customers" /></RequirePermission></LazyPage> },
          { path: 'distribution/customers/:customerId', element: <LazyPage><RequirePermission permission="distribution.customers.view"><DistributionCustomerDetailPage /></RequirePermission></LazyPage> },
          { path: 'distribution/catalog', element: <LazyPage><RequirePermission anyOf={['distribution.catalog.view', 'distribution.pricing.view']}><DistributionWorkspacePage module="catalog" /></RequirePermission></LazyPage> },
          { path: 'distribution/administration', element: <LazyPage><RequirePermission anyOf={['distribution.collections.view', 'distribution.checks.view', 'distribution.procurement.view', 'distribution.tax.view', 'distribution.integrations.view']}><DistributionWorkspacePage module="administration" /></RequirePermission></LazyPage> },
          { path: 'distribution/reports', element: <LazyPage><RequirePermission anyOf={['distribution.sales.view', 'distribution.warehouses.view', 'distribution.pricing.view']}><DistributionWorkspacePage module="reports" /></RequirePermission></LazyPage> },
          { path: 'distribution/configuration', element: <LazyPage><RequirePermission anyOf={['distribution.integrations.view', 'distribution.customers.view', 'distribution.sales.view']}><DistributionWorkspacePage module="configuration" /></RequirePermission></LazyPage> },
          { path: 'distribution/collections', element: <Navigate replace to="/distribution/administration?tab=collections" /> },
          { path: 'distribution/fiscal-operations', element: <Navigate replace to="/distribution/administration?tab=fiscal" /> },
          { path: 'distribution/exceptions', element: <Navigate replace to="/distribution?tab=exceptions" /> },
          { path: 'distribution/returns', element: <Navigate replace to="/distribution/deliveries?tab=returns" /> },
          { path: 'distribution/acopio', element: <Navigate replace to="/distribution/stock?tab=acopio" /> },
          { path: 'distribution/warehouses', element: <Navigate replace to="/distribution/stock?tab=warehouses" /> },
          { path: 'distribution/procurement', element: <Navigate replace to="/distribution/administration?tab=procurement" /> },
          { path: 'industrial', element: <LazyPage><IndustrialSolutionsPage view="overview" /></LazyPage> },
          { path: 'industrial/warehouse', element: <LazyPage><WmsPage /></LazyPage> },
          { path: 'warehouse', element: <Navigate replace to="/industrial/warehouse" /> },
          { path: 'warehouse/*', element: <Navigate replace to="/industrial/warehouse" /> },
          { path: 'wms', element: <LazyPage><WmsPage /></LazyPage> },
          { path: 'industrial/warehouse/counts', element: <LazyPage><RequirePermission permission="distribution.stock.handheld"><IndustrialInventoryCountsPage /></RequirePermission></LazyPage> },
          { path: 'industrial/logistics', element: <LazyPage><IndustrialSolutionsPage view="logistics" /></LazyPage> },
          { path: 'industrial/assets', element: <LazyPage><IndustrialSolutionsPage view="assets" /></LazyPage> },

          // Risk Intelligence (World Monitor)
          { path: 'risk', element: <LazyPage><RiskDashboardPage /></LazyPage> },
          { path: 'risk/dashboard', element: <LazyPage><RiskDashboardPage /></LazyPage> },
          
          // Weather Module
          { path: 'weather', element: <LazyPage><WeatherModule /></LazyPage> },
          
          // Sustainability
          { path: 'emissions/calculator', element: <LazyPage><EmissionsCalculatorPage /></LazyPage> },
          { path: 'web-analytics', element: <LazyPage><WebAnalyticsPage /></LazyPage> },

          // Settings (permission-gated)
          { path: 'settings', element: <LazyPage><SettingsPage /></LazyPage> },
          { path: 'settings/profile', element: <LazyPage><ProfilePage /></LazyPage> },
          { path: 'settings/users', element: <RequirePermission permission="users:read"><LazyPage><UsersPage /></LazyPage></RequirePermission> },
          { path: 'settings/roles', element: <RequirePermission permission="roles:read"><LazyPage><RolesPage /></LazyPage></RequirePermission> },
          { path: 'settings/organizations', element: <RequirePermission permission="organizations:read"><LazyPage><OrganizationsPage /></LazyPage></RequirePermission> },
          { path: 'settings/notifications', element: <LazyPage><NotificationsSettingsPage /></LazyPage> },
          { path: 'settings/security', element: <RequirePermission permission="security:read"><LazyPage><SecurityDashboard /></LazyPage></RequirePermission> },
          { path: 'settings/code-intelligence', element: <LazyPage><CodeIntelligencePage /></LazyPage> },
          { path: 'settings/multi-tenancy', element: <RequirePermission superuserOnly><LazyPage><MultiTenancyInfoPage /></LazyPage></RequirePermission> },
          { path: 'settings/multi-tenancy-overview', element: <RequirePermission superuserOnly><LazyPage><MultiTenancyOverviewPage /></LazyPage></RequirePermission> },
          { path: 'settings/multi-tenancy-audit', element: <RequirePermission superuserOnly><LazyPage><MultiTenancyAuditPage /></LazyPage></RequirePermission> },
          { path: 'settings/vertical', element: <RequirePermission superuserOnly><LazyPage><VerticalSettingsPage /></LazyPage></RequirePermission> },
          { path: 'settings/qr', element: <LazyPage><QRGeneratorPage /></LazyPage> },
          { path: 'settings/user-permissions/:userId', element: <RequirePermission permission="users:read"><LazyPage><UserMenuPermissionsPage /></LazyPage></RequirePermission> },
        ],
      },
      
      // Public Order Tracking Portal — no auth required
      { path: 'track/:trackingNumber', element: <LazyPage><TrackingPortalPage /></LazyPage> },
      { path: 'track', element: <LazyPage><TrackingPortalPage /></LazyPage> },

      // Feria Sitevinitech 2026 — public kiosk, no auth required
      { path: 'feria', element: <LazyPage><FeriaLandingPage /></LazyPage> },
      { path: 'feria/vinedos', element: <LazyPage><VinedosDemoPage /></LazyPage> },
      { path: 'feria/trazabilidad', element: <LazyPage><CampoTraceabilityPage /></LazyPage> },
      { path: 'feria/flota', element: <LazyPage><FeriaFlotaPage /></LazyPage> },
      { path: 'feria/bi', element: <LazyPage><FeriaBIPage /></LazyPage> },
      { path: 'feria/clima', element: <LazyPage><FeriaClimaPage /></LazyPage> },
      { path: 'feria/mapa', element: <LazyPage><FeriaRFIDPage /></LazyPage> },

      // Mobile Handheld — Chafon H105 (auth required, no sidebar/header)
      {
        path: 'mobile',
        element: <MobileLayout />,
        children: [
          { path: 'home',                        element: <LazyPage><MobileHomePage /></LazyPage>        },
          { path: 'h105/logistics',              element: <LazyPage><MobileH105LogisticsPage /></LazyPage> },
          { path: 'h105/stock',                  element: <LazyPage><MobileH105StockPage /></LazyPage> },
          { path: 'h105/warehouse',              element: <LazyPage><MobileH105WarehousePage /></LazyPage> },
          { path: 'h105/sales',                  element: <LazyPage><MobileH105SalesPage /></LazyPage> },
          { path: 'h105/rfid',                   element: <LazyPage><MobileH105RfidPage /></LazyPage> },
          { path: 'rental/returns',              element: <LazyPage><RentalReturnsPage /></LazyPage> },
          { path: 'h105/diagnostics',            element: <LazyPage><MobileH105DiagnosticsPage /></LazyPage> },
          { path: 'connect',                     element: <LazyPage><RequirePermission permission="communications.view"><MobileConnectPage /></RequirePermission></LazyPage> },
          { path: 'laundry/recepcion',           element: <LazyPage><MobileRecepcionPage /></LazyPage>  },
          { path: 'laundry/ciclo',               element: <LazyPage><MobileCicloPage /></LazyPage>      },
          { path: 'laundry/evento',              element: <LazyPage><MobileEventoPage /></LazyPage>     },
          { path: 'laundry/despacho',            element: <LazyPage><MobileDespachoPage /></LazyPage>   },
          { path: 'collection/home',             element: <LazyPage><MobileDriverHomePage /></LazyPage> },
          { path: 'collection/stop/:stopId',     element: <LazyPage><MobileColeccionPage /></LazyPage>  },
          // Microapp Handheld WMS (Demo)
          { path: 'wms/login',                   element: <LazyPage><MobileWmsLoginPage /></LazyPage>   },
          { path: 'wms',                         element: <LazyPage><MobileWmsMenuPage /></LazyPage>    },
          { path: 'wms/picking',                 element: <LazyPage><MobileWmsPickingPage /></LazyPage> },
          { path: 'wms/receive',                 element: <LazyPage><MobileWmsReceivePage /></LazyPage> },
          { path: 'wms/move',                    element: <LazyPage><MobileWmsMovePage /></LazyPage>    },
          { path: 'wms/query',                   element: <LazyPage><MobileWmsQueryPage /></LazyPage>   },
        ],
      },

      // Error pages
      { path: '401', element: <LazyPage><UnauthorizedPage /></LazyPage> },
      { path: '*', element: <LazyPage><NotFoundPage /></LazyPage> },
    ],
  },
])
